import { differenceInDays } from 'date-fns'
import type { Account } from '../../../common/types'
import {
  canManageBilling,
  isOneBufferOrganization,
  isPayingStripeCustomer,
} from '../../../common/utils/user'

// Keep in sync with domains/features/src/getAttributesFromAccount.ts
export const DAYS_BEFORE_SHOWING_FOUR_MONTH = 120
export const DAYS_BEFORE_SHOWING_TWO_MONTH = 60

/**
 * Map a `geid-monthly-to-annual` Split treatment to the day threshold the
 * banner should fire at, or `null` when the treatment shouldn't show a banner.
 * The `two-month` treatment is served to all eligible users; the `four-month`
 * arm (and its `on` alias) is kept for a planned follow-up experiment that
 * re-nudges users still on monthly billing at month 4.
 */
export const daysBeforeShowingForTreatment = (
  treatment: string,
): number | null => {
  switch (treatment) {
    case 'four-month':
    case 'on':
      return DAYS_BEFORE_SHOWING_FOUR_MONTH
    case 'two-month':
      return DAYS_BEFORE_SHOWING_TWO_MONTH
    default:
      return null
  }
}

/**
 * Shared eligibility for annual upgrade UI (banner, modal, deeplink).
 * Does not include feature flags or banner dismiss state.
 * Callers own the tenure threshold: entry points (banner, cancel-flow CTA)
 * pass their cohort's day threshold, while gates behind an already-gated
 * entry point (modal render, deeplink) pass 0.
 */
export const isEligibleForAnnualUpgrade = (
  user: Account,
  daysBeforeShowing: number,
): boolean => {
  if (!isOneBufferOrganization(user)) return false
  if (!isPayingStripeCustomer(user)) return false
  if (!canManageBilling(user)) return false

  const subscription = user.currentOrganization?.billing?.subscription
  if (!subscription) return false
  if (subscription.isCanceledAtPeriodEnd) return false

  // Only for monthly subscribers
  if (subscription.interval !== 'month') return false

  const startDate = subscription.startDate
  if (!startDate) return false

  const daysSinceStart = differenceInDays(new Date(), new Date(startDate))
  return daysSinceStart >= daysBeforeShowing
}
